// Pure grouping/search/bulk-select model for the per-app native-tool picker
// (FRONT-2101, `tool-picker-dialog.tsx`). The old filter matched a raw
// substring against name + description, so "web search" matched nothing
// (`web_search`, one token); `filterOfferableTools` tokenizes the query
// instead. The offerable catalogue (`tool-offer.ts`) is untouched.
import { humanizeToolName } from '~/lib/humanize-tool-name';

export interface GroupableTool {
  name: string;
  server?: string;
}

export interface SearchableTool extends GroupableTool {
  description: string;
  summary?: string;
}

export interface ToolGroupInfo {
  key: string;
  label: string;
}

export interface GroupedTools<T> {
  key: string;
  label: string;
  tools: T[];
}

const GROUP_DEFS: Array<{ key: string; label: string; test: (name: string) => boolean }> = [
  {
    key: 'web-images',
    label: 'Web & images',
    test: (name) => name === 'web_search' || name === 'generate_image',
  },
  { key: 'files', label: 'Files', test: (name) => name.startsWith('file_') },
  { key: 'folders', label: 'Folders', test: (name) => name.startsWith('folder_') },
  { key: 'projects', label: 'Projects & tasks', test: (name) => name.startsWith('work_') },
  {
    key: 'graph',
    label: 'Memory',
    test: (name) =>
      name.startsWith('graph_') ||
      name === 'entity_show' ||
      name === 'relationship_path' ||
      name === 'activity_show',
  },
  { key: 'knowledge', label: 'Company knowledge', test: (name) => name.startsWith('knowledge_') },
  { key: 'sketches', label: 'Sketches', test: (name) => name.startsWith('sketch_') },
];

const OTHER_GROUP: ToolGroupInfo = { key: 'other', label: 'Other' };

/** An MCP tool reads as its own name on the server, without the `mcp__<server>__` prefix. */
export function toolDisplayName(tool: GroupableTool): string {
  if (!tool.server) return humanizeToolName(tool.name);
  const original = tool.name.split('__').slice(2).join('__');
  return humanizeToolName(original || tool.name);
}

/** Falls back to "Other" — never drops an unrecognized tool. */
export function toolGroupFor(name: string, server?: string): ToolGroupInfo {
  // FRONT-2971: one group per MCP server, after Eddy's own groups.
  if (server) return { key: `mcp:${server}`, label: `${server} (runs as the visitor)` };
  const match = GROUP_DEFS.find((g) => g.test(name));
  return match ? { key: match.key, label: match.label } : OTHER_GROUP;
}

/** Empty groups are omitted rather than rendered empty. */
export function groupOfferableTools<T extends GroupableTool>(tools: T[]): GroupedTools<T>[] {
  const buckets = new Map<string, { label: string; tools: T[] }>();
  for (const tool of tools) {
    const group = toolGroupFor(tool.name, tool.server);
    const bucket = buckets.get(group.key);
    if (bucket) {
      bucket.tools.push(tool);
    } else {
      buckets.set(group.key, { label: group.label, tools: [tool] });
    }
  }
  const mcpKeys = [...buckets.keys()].filter((k) => k.startsWith('mcp:')).sort();
  const orderedKeys = [...GROUP_DEFS.map((g) => g.key), OTHER_GROUP.key, ...mcpKeys];
  const out: GroupedTools<T>[] = [];
  for (const key of orderedKeys) {
    const bucket = buckets.get(key);
    if (!bucket) continue;
    out.push({
      key,
      label: bucket.label,
      tools: [...bucket.tools].sort((a, b) => toolDisplayName(a).localeCompare(toolDisplayName(b))),
    });
  }
  return out;
}

function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function searchWords(search?: string | null): string[] {
  const normalized = normalizeForSearch(search ?? '');
  return normalized.length ? normalized.split(' ') : [];
}

function toolHaystack(tool: SearchableTool): string {
  const human = humanizeToolName(tool.name);
  const spacedName = tool.name.replace(/_/g, ' ');
  const group = toolGroupFor(tool.name).label;
  return normalizeForSearch(
    [human, tool.name, spacedName, tool.summary ?? '', tool.description, group].join(' '),
  );
}

export function filterOfferableTools<T extends SearchableTool>(
  tools: T[],
  search?: string | null,
): T[] {
  const words = searchWords(search);
  if (words.length === 0) return tools;
  return tools.filter((tool) => {
    const haystack = toolHaystack(tool);
    return words.every((word) => haystack.includes(word));
  });
}

export function selectAllVisible(current: string[], visible: string[]): string[] {
  const present = new Set(current);
  const result = [...current];
  for (const name of visible) {
    if (!present.has(name)) {
      present.add(name);
      result.push(name);
    }
  }
  return result;
}

export function deselectAllVisible(current: string[], visible: string[]): string[] {
  const visibleSet = new Set(visible);
  return current.filter((name) => !visibleSet.has(name));
}

// Drop any non-offerable name before a Save PUT — a stale allowlist entry
// is reported, never auto-pruned (`tool-offer.ts`, untouched here). This
// and `nextDraft` compare over the OFFERABLE catalogue, not `stale`, which
// can momentarily disagree right after a save prunes a stale entry.
export function buildSavePayload(current: string[], offerableNames: Set<string>): string[] {
  return current.filter((name) => offerableNames.has(name));
}

/** Order-insensitive set equality. */
export function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}

// What Save sends. A non-offerable name that was already saved is pruned
// (FRONT-1385 D6); one the owner newly ticked is kept, so if it dropped out of
// the catalogue mid-edit the server refuses the save instead of it silently
// vanishing behind a "saved" toast.
export function buildPutPayload(
  current: string[],
  saved: string[],
  offerableNames: Set<string>,
): string[] {
  const savedSet = new Set(saved);
  return current.filter((name) => offerableNames.has(name) || !savedSet.has(name));
}

// `null` ("not dirty") when `candidate`'s offerable subset matches `saved`'s.
export function nextDraft(
  candidate: string[],
  saved: string[],
  offerableNames: Set<string>,
): string[] | null {
  return sameSet(buildSavePayload(candidate, offerableNames), buildSavePayload(saved, offerableNames))
    ? null
    : candidate;
}
